import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ListChecks,
  Search,
  FolderOpen,
  Check,
  CheckSquare,
  Square,
  ArrowRight,
  Zap,
  ScanSearch,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useSources } from '@/features/sources/hooks/useSources';
import { useFolders } from '@/features/sources/hooks/useFolders';
import { useCriteria } from '@/features/criteria/hooks/useCriteria';
import { useHasCredits } from '@/features/dashboard/hooks/useCredits';
import { estimateScreeningCredits } from '@/features/screening/hooks/useScreenSources';
import { CRITERION_TYPE_LABELS } from '@/features/criteria/types';
import type { Criterion } from '@/features/criteria/types';

interface ScreeningSetupProps {
  onStart: (sourceIds: string[], criteriaIds: string[]) => void;
  isStarting: boolean;
}

export function ScreeningSetup({ onStart, isStarting }: ScreeningSetupProps) {
  const navigate = useNavigate();
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [selectedCriteriaIds, setSelectedCriteriaIds] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [folderId, setFolderId] = useState<string>('all');

  const { data: sources = [], isLoading: sourcesLoading } = useSources(
    folderId && folderId !== 'all' ? { folder_id: folderId } : {},
  );
  const { data: folders = [], isLoading: foldersLoading } = useFolders();
  const { data: criteria = [], isLoading: criteriaLoading } = useCriteria();

  const creditsNeeded = estimateScreeningCredits(selectedSourceIds.length);
  const { hasCredits, credits, isLoading: creditsLoading } = useHasCredits(creditsNeeded);

  // Accept any source with title or abstract (no need for extracted_text)
  const readySources = useMemo(() => {
    return sources.filter((s) => s.title || s.abstract);
  }, [sources]);

  const filteredSources = useMemo(() => {
    let result = readySources;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.file_name.toLowerCase().includes(q) ||
          (s.title && s.title.toLowerCase().includes(q)) ||
          (s.authors && s.authors.some((a: string) => a.toLowerCase().includes(q))),
      );
    }
    return result;
  }, [readySources, search, folderId]);

  const allSelected =
    filteredSources.length > 0 &&
    filteredSources.every((s) => selectedSourceIds.includes(s.id));

  function toggleSource(id: string) {
    setSelectedSourceIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  }

  function toggleAll() {
    if (allSelected) {
      const filteredIds = new Set(filteredSources.map((s) => s.id));
      setSelectedSourceIds((prev) => prev.filter((id) => !filteredIds.has(id)));
    } else {
      const newIds = new Set([...selectedSourceIds, ...filteredSources.map((s) => s.id)]);
      setSelectedSourceIds([...newIds]);
    }
  }

  function toggleCriterion(id: string) {
    setSelectedCriteriaIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id],
    );
  }

  function selectAllCriteria() {
    if (selectedCriteriaIds.length === criteria.length) {
      setSelectedCriteriaIds([]);
    } else {
      setSelectedCriteriaIds(criteria.map((c) => c.id));
    }
  }

  const isReady = selectedSourceIds.length > 0 && selectedCriteriaIds.length > 0 && hasCredits;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Sources */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-900">1. Select Sources</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={toggleAll} disabled={filteredSources.length === 0}>
            {allSelected ? (
              <><CheckSquare className="mr-1 h-4 w-4" />Deselect All</>
            ) : (
              <><Square className="mr-1 h-4 w-4" />Select All</>
            )}
          </Button>
        </div>

        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search sources..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={folderId} onValueChange={setFolderId}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All folders" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All folders</SelectItem>
              {folders.map((folder) => (
                <SelectItem key={folder.id} value={folder.id}>{folder.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="max-h-[400px] overflow-y-auto rounded-lg border border-slate-200">
          {sourcesLoading || foldersLoading ? (
            <div className="space-y-2 p-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : filteredSources.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-slate-400">
              <FileText className="mb-2 h-8 w-8" />
              <p className="text-sm">
                {readySources.length === 0 ? 'No sources with title or abstract' : 'No sources match your filters'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredSources.map((source) => {
                const isSelected = selectedSourceIds.includes(source.id);
                return (
                  <button
                    key={source.id}
                    type="button"
                    onClick={() => toggleSource(source.id)}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-slate-50',
                      isSelected && 'bg-indigo-50 hover:bg-indigo-50',
                    )}
                  >
                    <div className={cn(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                      isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300',
                    )}>
                      {isSelected && <Check className="h-3.5 w-3.5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-slate-900">
                        {source.title || source.file_name}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {source.authors && source.authors.length > 0 && (
                          <span className="truncate">
                            {(source.authors as string[]).slice(0, 2).join(', ')}
                            {(source.authors as string[]).length > 2 && ' et al.'}
                          </span>
                        )}
                        {source.publication_year && <span>({source.publication_year})</span>}
                      </div>
                    </div>
                    {folderId && folderId !== 'all' && (
                      <Badge variant="outline" className="shrink-0 text-xs">
                        <FolderOpen className="mr-1 h-3 w-3" />
                        {folders.find((f) => f.id === folderId)?.name ?? 'Folder'}
                      </Badge>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <p className="mt-2 text-xs text-slate-400">
          {selectedSourceIds.length} of {readySources.length} sources selected
        </p>
      </div>

      {/* Criteria */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-indigo-600" />
            <h2 className="text-sm font-semibold text-slate-900">2. Select Criteria</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={selectAllCriteria} disabled={criteria.length === 0}>
            {selectedCriteriaIds.length === criteria.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>

        <div className="max-h-[400px] space-y-2 overflow-y-auto">
          {criteriaLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 animate-pulse rounded-md bg-slate-100" />
              ))}
            </div>
          ) : criteria.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-slate-400">
              <ListChecks className="mb-2 h-8 w-8" />
              <p className="text-sm">No criteria yet</p>
              <Button variant="link" size="sm" onClick={() => navigate('/criteria')}>
                Create criteria <ArrowRight className="ml-1 h-3 w-3" />
              </Button>
            </div>
          ) : (
            criteria.map((criterion: Criterion) => {
              const isSelected = selectedCriteriaIds.includes(criterion.id);
              return (
                <button
                  key={criterion.id}
                  type="button"
                  onClick={() => toggleCriterion(criterion.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors',
                    isSelected ? 'border-indigo-200 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50',
                  )}
                >
                  <div className={cn(
                    'flex h-5 w-5 shrink-0 items-center justify-center rounded border',
                    isSelected ? 'border-indigo-600 bg-indigo-600' : 'border-slate-300',
                  )}>
                    {isSelected && (
                      <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                        <path d="M2.5 6L5 8.5L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">{criterion.name}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Badge variant="outline" className="text-[10px]">
                        {CRITERION_TYPE_LABELS[criterion.type]}
                      </Badge>
                      <span>Weight: {criterion.weight}/5</span>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Launch panel */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <ScanSearch className="h-4 w-4 text-indigo-600" />
          <h2 className="text-sm font-semibold text-slate-900">3. Launch AI Screening</h2>
        </div>

        <div className="space-y-3 mb-6">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <p className="text-xs font-medium text-emerald-800">GPT-4o-mini Screening</p>
            <p className="mt-0.5 text-xs text-emerald-600">
              Title & abstract only. 10 parallel batches of 50.
            </p>
          </div>

          <div className="space-y-1.5 text-xs">
            <div className="flex items-center justify-between text-slate-600">
              <span>Sources</span>
              <span className="font-medium">{selectedSourceIds.length}</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>Criteria</span>
              <span className="font-medium">{selectedCriteriaIds.length}</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>Batches</span>
              <span className="font-medium">{Math.ceil(selectedSourceIds.length / 50) || 0}</span>
            </div>
            <div className="flex items-center justify-between text-slate-600">
              <span>Estimated time</span>
              <span className="font-medium">
                ~{Math.max(1, Math.ceil(Math.ceil(selectedSourceIds.length / 50) / 10) * 45)}s
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <Badge
            variant="outline"
            className={cn('text-xs', !creditsLoading && !hasCredits && 'border-rose-300 text-rose-600')}
          >
            <Zap className="mr-1 h-3 w-3" />
            {creditsLoading ? '...' : `${credits} credits`}
          </Badge>
          <span className="text-xs text-slate-500">
            Cost: {creditsNeeded} credit{creditsNeeded !== 1 ? 's' : ''}
          </span>
        </div>

        <Button
          onClick={() => onStart(selectedSourceIds, selectedCriteriaIds)}
          disabled={!isReady || isStarting}
          className="w-full"
          size="lg"
        >
          <ScanSearch className="mr-2 h-4 w-4" />
          Start AI Screening ({creditsNeeded} credit{creditsNeeded !== 1 ? 's' : ''})
        </Button>

        {!creditsLoading && !hasCredits && selectedSourceIds.length > 0 && (
          <p className="mt-2 text-center text-xs text-rose-500">
            Not enough credits. Need {creditsNeeded}, have {credits}.
          </p>
        )}
      </div>
    </div>
  );
}
